import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Clock, Award } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const LABEL_STYLE = {
  'Excellent':          { bg:'#f0fdf4', text:'#16a34a', border:'#86efac' },
  'Very Good':          { bg:'#eff6ff', text:'#1d4ed8', border:'#93c5fd' },
  'Good':               { bg:'#fefce8', text:'#ca8a04', border:'#fde047' },
  'Average':            { bg:'#fff7ed', text:'#ea580c', border:'#fdba74' },
  'Needs Improvement':  { bg:'#fef2f2', text:'#dc2626', border:'#fca5a5' },
};

export default function AssessmentResult() {
  const navigate = useNavigate();
  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const attemptId = parseInt(localStorage.getItem('finirio_assess_id') || '0');
  const token     = localStorage.getItem('finirio_assess_tok') || '';

  useEffect(() => {
    if (!attemptId || !token) { navigate('/assessment'); return; }

    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/assessment/result/${attemptId}?token=${encodeURIComponent(token)}`);
        setResult(res.data);
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (detail.includes('not yet')) {
          // Auto-submit then reload result
          try {
            const res2 = await axios.post(`${API}/api/assessment/submit`, {
              attempt_id: attemptId, session_token: token,
            });
            setResult(res2.data);
          } catch {
            setError('Could not load your results. Please contact HR.');
          }
        } else {
          setError(detail || 'Could not load results. Please contact HR.');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <p className="text-slate-500">Loading your results…</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center px-4">
        <p className="text-red-600 mb-4">{error}</p>
        <Link to="/" className="text-[#1e73be] underline text-sm">← Go to Home</Link>
      </div>
    </div>
  );

  const { candidate, score, timing } = result;
  const label = score.performance_label || 'Average';
  const lc    = LABEL_STYLE[label] || LABEL_STYLE['Average'];

  const sections = [
    { key: 'english_pct', label: 'English',               color: '#1e73be' },
    { key: 'maths_pct',   label: 'Mathematics',           color: '#2ea690' },
    { key: 'logical_pct', label: 'Logical Reasoning',     color: '#7c3aed' },
    { key: 'reading_pct', label: 'Reading Comprehension', color: '#ea580c' },
  ].filter(s => score[s.key] != null);

  const taken = timing.time_taken_seconds;
  const timeFmt = taken ? `${Math.floor(taken / 60)}m ${taken % 60}s` : '—';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 py-3 px-6">
        <div className="max-w-[800px] mx-auto flex items-center justify-between">
          <Link to="/">
            <img src="https://customer-assets.emergentagent.com/job_site-creator-3034/artifacts/pbfdilsv_Finirio.JPG"
              alt="Finirio" className="h-8 object-contain" />
          </Link>
          <Link to="/" className="text-sm text-[#1e73be] hover:underline">← Back to Website</Link>
        </div>
      </div>

      <div className="max-w-[800px] mx-auto px-4 py-8 space-y-5">
        {/* Score card */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="p-8 text-center text-white" style={{ background: 'linear-gradient(135deg,#0f3d78,#1e73be)' }}>
            <Award className="w-12 h-12 text-white/70 mx-auto mb-2" />
            <h1 className="text-2xl font-bold">Assessment Complete!</h1>
            <p className="text-white/70 mt-1">{candidate.full_name}</p>
            {candidate.phone && <p className="text-white/50 text-sm">{candidate.phone}</p>}
          </div>

          <div className="p-8 text-center">
            <div className="text-7xl font-black mb-3" style={{ color: lc.text }}>
              {score.overall_pct?.toFixed(1)}%
            </div>
            <span className="inline-flex items-center gap-2 px-5 py-1.5 rounded-full border text-sm font-semibold"
              style={{ backgroundColor: lc.bg, color: lc.text, borderColor: lc.border }}>
              {label}
            </span>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-7">
              <StatBox icon={<CheckCircle className="w-5 h-5 text-green-500" />}
                label="Correct" value={score.correct_count} color="text-green-600" />
              <StatBox icon={<XCircle className="w-5 h-5 text-red-500" />}
                label="Wrong"   value={score.wrong_count}   color="text-red-600" />
              <StatBox icon={<span className="text-lg text-slate-400">⬜</span>}
                label="Unanswered" value={score.unanswered_count} color="text-slate-600" />
              <StatBox icon={<Clock className="w-5 h-5 text-[#1e73be]" />}
                label="Time Taken" value={timeFmt} color="text-[#1e73be]" />
            </div>
          </div>
        </div>

        {/* Section breakdown */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h2 className="font-semibold text-slate-800 mb-5">Section-wise Performance</h2>
          <div className="space-y-5">
            {sections.map(s => {
              const pct = score[s.key] || 0;
              return (
                <div key={s.key}>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-sm font-medium text-slate-700">{s.label}</span>
                    <span className="text-sm font-bold" style={{ color: s.color }}>{pct.toFixed(1)}%</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${pct}%`, backgroundColor: s.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Message */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center shadow-sm">
          <p className="text-slate-700 mb-2">
            🎉 Your assessment has been successfully recorded. Our HR team will review your results and get in touch with you.
          </p>
          <p className="text-xs text-slate-400 mt-3">
            Candidate Reference: #{attemptId} &nbsp;|&nbsp;
            {candidate.designation_applied && `Applied for: ${candidate.designation_applied}`}
          </p>
        </div>
      </div>
    </div>
  );
}

const StatBox = ({ icon, label, value, color }) => (
  <div className="bg-slate-50 rounded-xl p-3 text-center">
    <div className="flex justify-center mb-1">{icon}</div>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
    <p className="text-xs text-slate-500 mt-0.5">{label}</p>
  </div>
);
